import ReactPaginate from 'react-paginate'
import { useRouter } from 'next/router'
import { addParams } from 'utils/addParams'
import cn from 'classnames'
import { calculatePage } from 'url-pagination'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid'

export type Props = {
  total: number
  onChange: (page: number) => void
  page: number
  perPage: number
  includeHref: boolean
  pageKey?: string
  showEmpty?: boolean
}

export default function Pagination({
  page,
  onChange,
  total,
  includeHref,
  perPage,
  pageKey = 'page',
  showEmpty = true
}: Props) {
  const pageCount = calculatePage({ offset: total, perPage })
  const hasPrev = page > 0
  const hasNext = page + 1 < pageCount
  const router = useRouter()
  const hrefBuilder = includeHref
    ? (pageIndex: number) => addParams(router, { [pageKey]: pageIndex })
    : undefined
  const linkClassName =
    'relative inline-flex items-center border-l border-gray-300 bg-white text-sm font-medium hover:bg-orange-50 py-2 outline-none-mouse'

  if (pageCount <= 1 && !showEmpty) return null

  return (
    <ReactPaginate
      activeClassName={cn('font-bold')}
      activeLinkClassName="bg-orange-100 font-bold"
      breakClassName="relative inline-flex items-center px-4 py-2 border-l border-gray-300 bg-white text-sm font-medium text-gray-700 hidden md:block"
      breakLabel="..."
      containerClassName="relative z-0 inline-flex rounded-md shadow-sm -space-x-px overflow-hidden border border-gray-300 border-solid"
      forcePage={page}
      hrefBuilder={hrefBuilder}
      marginPagesDisplayed={1}
      nextClassName={cn({ hidden: !hasNext })}
      nextLabel={<ChevronRightIcon className="w-5" />}
      nextLinkClassName={cn(linkClassName, 'text-gray-500 px-2 rounded-r-md')}
      onPageChange={({ selected }) => {
        onChange(selected)
      }}
      pageCount={pageCount}
      pageLinkClassName={cn(linkClassName, 'text-gray-700 px-4')}
      pageRangeDisplayed={2}
      previousClassName={cn({ hidden: !hasPrev })}
      previousLabel={<ChevronLeftIcon className="w-5" />}
      previousLinkClassName={cn(
        linkClassName,
        'text-gray-500 px-2 rounded-l-md border-none'
      )}
    />
  )
}
